import { observer } from 'mobx-react'
import { useCallback, useEffect, useState } from 'react'
import { Page, PageMainSection, PageProps } from '../components/Page/Page'
import FetchingWrapper from '../components/Page/FetchingWrapper'
import { VehicleForm } from '../components/Vehicles/VehicleForm'
import { VehiclesTable } from '../components/Vehicles/VehiclesTable'
import { VehicleTableHeader } from '../components/Vehicles/VehicleTableHeader'
import { filterVehiclesBySearch, getInstalledProfileTitle } from '../components/Vehicles/vehiclesSearch'
import { getNextVehiclesSort, sortVehicles, VehiclesSort, VehiclesSortKey } from '../components/Vehicles/vehiclesSort'
import { combinedOperationStatus } from '../models/common/OperationStatus'
import { PermissionTypes, UserPermissionsKeyTypes } from '../models/Users/UserModel'
import { withCustomTooltips } from '../components/Tooltip/TooltipManager'
import { isOversightControl } from '../stores/rootStore'
import { 
    WithVehiclesRoute,
    vehiclesPageLink, 
    vehiclesPageWithCreateNewVehicleLink, 
    getLinkForVehiclesPageWithSelectedVehicle,
    getLinkForVehiclesPageWithSelectedVehicleDetails, 
    getLinkForVehiclesPageWithSelectedVehicleParts,
    getLinkForVehiclesPageWithSelectedVehicleAndPart,
    getLinkForVehiclesPageWithSpecificVehicleAndNewPart,
    getLinkForVehiclesPageWithSpecificVehicleAndEditedPart,
    getLinkForVehiclesPageWithSelectedVehicleDiscrepancyReports,
    getLinkForVehiclesPageWithSelectedVehicleAndDiscrepancyReport, 
} from '../router'
import type { WithCenterSettingsStore, WithChecklistsStore, WithDiscrepancyReportsStore, WithPartNumbersStore, WithProfilesStore, WithProfileUpdateRequestsStore, WithVehiclesStore, WithCommonSettingsStoreStore } from '../stores/RootStoreModel'
import { showNotificationForStatus, showNotificationsForStatusMap } from '../utils/NotificationUtils'


interface VehiclesPageProps extends
    PageProps,
    WithCenterSettingsStore,
    WithChecklistsStore,
    WithVehiclesStore,
    WithDiscrepancyReportsStore,
    WithPartNumbersStore,
    WithProfilesStore,
    WithProfileUpdateRequestsStore,
    WithCommonSettingsStoreStore,
    WithVehiclesRoute {}

export const VehiclesPage = observer(({ vehiclesStore, centerSettingsStore, checklistsStore, partNumbersStore, discrepancyReportsStore, profilesStore, profileUpdateRequestsStore, commonSettingsStore, currentRoute, ...pageProps }: VehiclesPageProps) => {

    // null keeps the order of the vehicles from the backend
    const [vehiclesSort, setVehiclesSort] = useState<VehiclesSort | null>(null)

    const initializeVehicles = useCallback(async () => {
        // Profiles (OTA) exist only in oversight control. Fetched again on every visit while failing.
        if (isOversightControl()) {
            profilesStore.fetchProfilesIfNeeded()
        }
        if (checklistsStore.fetchStatus.isSuccess &&
            partNumbersStore.fetchStatus.isSuccess &&
            discrepancyReportsStore.fetchStatus.isSuccess &&
            vehiclesStore.fetchStatus.isSuccess) {
            // Don't re-fetch if already initialized. 
            // User should reload page to refetch vehicles.
            return 
        }
        await Promise.all([
            checklistsStore.fetchAndSetChecklists(), // Refresh checklists because vehicles will contain reference to those checklists
            partNumbersStore.fetchAndSetPartNumbers(), // Refresh Part numbers
            discrepancyReportsStore.fetchAndSetDiscrepancyReports(), // Fetch all discrepancy reports, to show in the reports tab
            ...(isOversightControl() ? [centerSettingsStore.fetchCenters()] : []),
        ])
        if (!checklistsStore.fetchStatus.isSuccess) {
            console.warn('Loading checklists failed, so we cannot load vehicles')
            return
        }
        if (!partNumbersStore.fetchStatus.isSuccess) {
            console.warn('Loading part numbers failed, so we cannot load vehicles')
            return
        }
        if (!discrepancyReportsStore.fetchStatus.isSuccess) {
            console.warn('Loading discrepancy reports failed, so we cannot show vehicles')
            return
        }
        return vehiclesStore.fetchAndSetVehiclesAndMaintenance() 
    }, [])

    useEffect(() => {
        void (async () => {
            await initializeVehicles()
            switch (currentRoute.name) {
                case vehiclesPageLink.name:
                    vehiclesStore.clearSelectedVehicle()
                    break
                case vehiclesPageWithCreateNewVehicleLink.name:
                    vehiclesStore.createNewVehicle()
                    break
                case getLinkForVehiclesPageWithSelectedVehicle(0).name:
                    getLinkForVehiclesPageWithSelectedVehicleParts(currentRoute.params.vehicleId).replace() // Replace, so browser back skips this redirect
                    break                    
                case getLinkForVehiclesPageWithSelectedVehicleDetails(0).name:
                    vehiclesStore.setSelectedVehicle(currentRoute.params.vehicleId)
                    void vehiclesStore.fetchVehicleParts(currentRoute.params.vehicleId) // Fetch parts already, although we inly show the part counter, not all info
                    void vehiclesStore.fetchVehicleModuleVersions(currentRoute.params.vehicleId)
                    if (isOversightControl()) void vehiclesStore.fetchVehicleRUL(currentRoute.params.vehicleId)
                    break
                case getLinkForVehiclesPageWithSelectedVehicleParts(0).name:
                    vehiclesStore.setSelectedVehicle(currentRoute.params.vehicleId)
                    void vehiclesStore.fetchVehicleParts(currentRoute.params.vehicleId)
                    break
                case getLinkForVehiclesPageWithSelectedVehicleAndPart(0, 0).name:
                    const didSelectVehicle = vehiclesStore.setSelectedVehicle(currentRoute.params.vehicleId)
                    if (!didSelectVehicle) break
                    await vehiclesStore.fetchVehicleParts(currentRoute.params.vehicleId) // Must await for fetching parts to complete before setting selected part
                    vehiclesStore.setSelectedPart(currentRoute.params.partId)
                    break
                case getLinkForVehiclesPageWithSpecificVehicleAndNewPart(0).name:
                    vehiclesStore.setSelectedVehicle(currentRoute.params.vehicleId)
                    await vehiclesStore.fetchVehicleParts(currentRoute.params.vehicleId) // Must await for fetching parts to complete before setting selected part
                    vehiclesStore.createNewPartInstance()
                    break
                case getLinkForVehiclesPageWithSpecificVehicleAndEditedPart(0, 0).name:
                    vehiclesStore.setSelectedVehicle(currentRoute.params.vehicleId)
                    await vehiclesStore.fetchVehicleParts(currentRoute.params.vehicleId) // Must await for fetching parts to complete before setting selected part
                    vehiclesStore.editPartInstanceOfSelectedVehicle(currentRoute.params.partId)
                    break
                case getLinkForVehiclesPageWithSelectedVehicleDiscrepancyReports(0).name:
                case getLinkForVehiclesPageWithSelectedVehicleAndDiscrepancyReport(0, 0).name: 
                    vehiclesStore.setSelectedVehicle(currentRoute.params.vehicleId)
                    break
                
            }
        })()
    }, [vehiclesStore, currentRoute])

    const { 
        vehicles, 
        vehicleStates, 
        vehicleParts,
        vehicleModuleVersionInformation,
        vehicleMaintenance,

        selectedVehicle, 
        vehicleCloneForEditing,
        saveEditedVehicle,
        saveStatus, 


        selectedPartInstance,
        partInstanceLogs,
        fetchPartInstanceLogs,

        editedPartInstance,
        
        markPartAsUnAirworthy,
        markPartAsInspected,
        removePartFromVehicle,
        assignToDeployment: assignToDeployment,
        assignToDeploymentStatuses: assignToDeploymentStatuses,
        vehicleRUL,

        fetchModuleVersionInfoStatuses,
        fetchVehiclePartsStatuses,
        fetchVehicleMaintenanceStatuses,
        fetchVehiclesStateStatus,
        fetchPartInstanceLogStatuses,
        markPartAsInspectedStatuses,
        markPartAsUnAirworthyStatuses,
        removePartFromVehicleStatuses,
        
        saveEditedPartInstance,
        saveEditedPartInstanceStatus,
        
        setEditedPartInstanceFromSerial,
    } = vehiclesStore

    showNotificationForStatus(saveStatus, `Error saving vehicle`,`Vehicle saved successfully`, true)
    showNotificationForStatus(fetchVehiclesStateStatus, `Error fetching vehicles states`)
    showNotificationForStatus(vehiclesStore.changeServiceStatusStatus, `Error changing vehicle status`,`Vehicle status changed successfully`, true)
    showNotificationForStatus(saveEditedPartInstanceStatus, `Error saving part instance`,`Part instance saved successfully`, true)
    showNotificationForStatus(discrepancyReportsStore.resolveStatus, `Error when trying to resolve discrepancy report`,`Resolved discrepancy report successfully`, true)
    
    
    showNotificationsForStatusMap(fetchModuleVersionInfoStatuses, `Error fetching module versions for vehicle`)
    showNotificationsForStatusMap(fetchVehiclePartsStatuses, `Error fetching vehicle parts for vehicle`)
    showNotificationsForStatusMap(fetchVehicleMaintenanceStatuses, `Error refreshing vehicle maintenance status for vehicle`)
    showNotificationsForStatusMap(fetchPartInstanceLogStatuses, `Error fetching part actions log for part instance`)
    showNotificationsForStatusMap(markPartAsInspectedStatuses, `Error marking part as inspected`,`Marked part as inspected`, true)
    showNotificationsForStatusMap(markPartAsUnAirworthyStatuses, `Error marking part as unairworthy`,`Marked part as unairworthy`, true)
    showNotificationsForStatusMap(removePartFromVehicleStatuses, `Error removing part from vehicle`,`Removed part from vehicle`, true)
    showNotificationForStatus(profileUpdateRequestsStore.createStatus, `Error requesting profile update`, `Profile update requested`)
    showNotificationsForStatusMap(assignToDeploymentStatuses, `Error transferring vehicle to center`,`Vehicle transferred successfully`, true)

    const selectedVehicleId = selectedVehicle?.id ?? null
    const aggregatedFetchStatus = combinedOperationStatus(
        checklistsStore.fetchStatus,
        partNumbersStore.fetchStatus,
        discrepancyReportsStore.fetchStatus,
        vehiclesStore.fetchStatus
    )
    const isShowingVehiclesList = currentRoute.name === vehiclesPageLink.name
    // Profile updates (OTA) exist only in oversight control, and require OTA edit permission there
    const canRequestProfileUpdate = isOversightControl() && commonSettingsStore.hasPermission(UserPermissionsKeyTypes.Ota, PermissionTypes.Edit)
    const refreshVehicleProfiles = () => { void vehiclesStore.refreshVehicleProfiles() }
    const searchedVehicles = vehicles ? filterVehiclesBySearch(vehicles, vehiclesStore.searchText, profilesStore) : null
    const shownVehicles = searchedVehicles && vehiclesSort
        ? sortVehicles(searchedVehicles, vehiclesSort, {
            getProfileTitle: vehicle => getInstalledProfileTitle(vehicle, profilesStore),
            getStatus: vehicle => vehicleStates.get(vehicle.id.toString())?.serviceStatus,
        })
        : searchedVehicles
    // Vehicles hidden by the search are unchecked, so the selection always matches the shown rows
    const shownVehicleIdsKey = shownVehicles?.map(vehicle => vehicle.id).join(',')
    useEffect(() => {
        if (shownVehicles) {
            vehiclesStore.uncheckVehiclesExcept(shownVehicles.map(vehicle => vehicle.id))
        }
    }, [shownVehicleIdsKey])
    return (
    <Page {...{ ...pageProps, ...withCustomTooltips }}>
        <PageMainSection>
            <FetchingWrapper {...{
                fetchStatus: aggregatedFetchStatus,
                inProgressTitle: 'Loading vehicles...', 
                errorMessagePrefix: 'Error loading vehicles', 
                onRetry: initializeVehicles
            }}>
                { isShowingVehiclesList ?
                    (shownVehicles ? 
                        <>
                            <VehicleTableHeader {...{ shownVehicleCount: shownVehicles.length, filters: vehiclesStore.filters }} />
                            <VehiclesTable {...{
                                vehicles: shownVehicles,
                                vehicleStates,
                                vehicleMaintenance,
                                centers: centerSettingsStore.centers,
                                assignToDeployment,
                                profilesStore,
                                profileUpdateRequestsStore,
                                onProfileUpdateRequested: refreshVehicleProfiles,
                                sort: vehiclesSort,
                                onSort: (sortKey: VehiclesSortKey) => { setVehiclesSort(getNextVehiclesSort(vehiclesSort, sortKey)) },
                                checkedVehicleIds: vehiclesStore.checkedVehicleIds,
                                setVehicleChecked: vehiclesStore.setVehicleChecked,
                                setCheckedVehicleIds: vehiclesStore.setCheckedVehicleIds,
                                canRequestProfileUpdate,
                                setVehiclesServiceStatus: vehiclesStore.setVehiclesServiceStatus,
                            }}/>
                        </> : 
                        <span>Oops, no vehicles present :(</span>) :
                    vehicleCloneForEditing !== null &&
                    <VehicleForm {...{ 
                        currentRoute,
                        saveStatus,
                        spinnerTitle: 'Saving vehicle...',
                        editedVehicle: vehicleCloneForEditing,
                        vehicleState: selectedVehicleId !== null ? vehicleStates.get(selectedVehicleId.toString()) : undefined,
                        vehicleParts: selectedVehicleId !== null ? vehicleParts.get(selectedVehicleId.toString()) : undefined,
                        vehicleDiscrepancyReports: selectedVehicleId !== null ? discrepancyReportsStore.discrepancyReports?.filter(report => report.vehicle_id === selectedVehicleId) : undefined,
                        vehicleModuleVersionInformation: selectedVehicleId !== null ? vehicleModuleVersionInformation.get(selectedVehicleId?.toString()) : undefined,
                        centerChecklists: checklistsStore.checklists ?? [],
                        onSaveVehicle: saveEditedVehicle,

                        setVehiclesServiceStatus: vehiclesStore.setVehiclesServiceStatus,
                        
                        selectedPartId: selectedPartInstance?.id ?? null,
                        
                        partInstanceLogs,
                        fetchPartInstanceLogStatuses,
                        fetchPartInstanceLogs,
                        markPartAsUnAirworthy,
                        markPartAsInspected,
                        removePartFromVehicle,

                        editedPartInstance,
                        partNumbers: partNumbersStore.partNumbers ?? [],

                        saveEditedPartInstance,
                        saveEditedPartInstanceStatus,
                        setEditedPartInstanceFromSerial,

                        resolveDiscrepancyReport: discrepancyReportsStore.resolveDiscrepancyReport,
                        assignToDeployment: assignToDeployment,
                        vehicleRUL: selectedVehicleId !== null ? vehicleRUL.get(selectedVehicleId.toString()) : undefined,
                        centers: centerSettingsStore.centers,
                        profilesStore,
                        profileUpdateRequestsStore,
                        onProfileUpdateRequested: refreshVehicleProfiles,
                        canRequestProfileUpdate,
                    }} /> 
                }
            </FetchingWrapper>
        </PageMainSection>
    </Page>
)})
